import { useCallback, useEffect, useState } from "react";
import { getTransactionSummaryAction } from "../../Actions/TransactionActions";
import { MyWifiIcon, MyWifiOffIcon, MyRefreshIcon, MyReceiptLongIcon, MyTaskAltIcon, MyHourglassEmptyIcon, MyDoNotDisturbOnIcon } from "../../Assets/svg/MyIcons";
import PaycentralTransactionCards from "../PaycentralTransactions/TransactionCards"
import { listTripolaTransactionDataAction } from "../../Actions/TripolaPlanActions"

const StatCard = ({ title, count, amount, icon: Icon, color, loading, colClass }) => {
  return (
    <div className={colClass}>
      <div className="card h-100 shadow-sm rounded-3">
        {loading ? (
          <div className="d-flex align-items-center justify-content-center p-10">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: 32, height: 32 }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="card-body d-flex flex-column justify-content-between p-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-5">
              <div
                className="symbol w-55px h-55px d-flex justify-content-center align-items-center rounded-circle border"
                style={{
                  backgroundColor: `${color}20`,
                  borderColor: `${color}40`,
                  color,
                }}
              >
                <Icon size={28} />
              </div>
              <div className="fs-2x fw-bold text-dark">{count?.toLocaleString()}</div>
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-semibold text-gray-600 fs-5">{title}</div>
              {amount !== undefined && <div className="fs-6 fw-bold text-dark">CI$ {amount}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TransactionCards = ({
  handleSnackBar = () => { },
  startDate,
  endDate,
  statusFilter,
  tenantFilter,
  searchValue,
  amountFilter,
  shouldRefresh = 0,
  transactionType
}) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: { count: 0, amount: 0 },
    success: { count: 0, amount: 0 },
    pending: { count: 0, amount: 0 },
    refund: { count: 0, amount: 0 },
    failed: { count: 0, amount: 0 },
    online: { count: 0, amount: 0 },
    offline: { count: 0, amount: 0 },
  });

  const safe = (val, fallback = 0) => val ?? fallback;

  // Map data for Qbot transactions (original format)
  const mapQbotData = useCallback(
    (data) => ({
      total: {
        count: safe(data?.totalTransactionCount),
        amount: safe(data?.totalTransactionAmount),
      },
      success: {
        count: safe(data?.totalSuccessTransactionCount),
        amount: safe(data?.totalSuccessTransactionAmount),
      },
      pending: {
        count: safe(data?.totalPendingTransactionCount),
        amount: safe(data?.totalPendingTransactionAmount),
      },
      refund: {
        count: safe(data?.totalRefundTransactionCount),
        amount: safe(data?.totalRefundTransactionAmount),
      },
      failed: {
        count: safe(data?.totalFailedTransactionCount),
        amount: safe(data?.totalFailedTransactionAmount),
      },
      online: {
        count: safe(data?.totalOnlinePaymentsCount),
        amount: safe(data?.totalOnlinePaymentsAmount),
      },
      offline: {
        count: safe(data?.totalOfflinePaymentsCount),
        amount: safe(data?.totalOfflinePaymentsAmount),
      },
    }), []);

  const mapTripolaData = useCallback((data) => {
    // console.log("DATA RECEIVED IN MAPPER", data);

    return {
      total: {
        count: safe(data?.dashboardStats?.overall?.count),
        amount: safe(data?.dashboardStats?.overall?.amount),
      },
      success: {
        count: safe(data?.dashboardStats?.success?.count),
        amount: safe(data?.dashboardStats?.success?.amount),
      },
      pending: {
        count: safe(data?.dashboardStats?.pending?.count),
        amount: safe(data?.dashboardStats?.pending?.amount),
      },
      refund: {
        count: safe(data?.dashboardStats?.refunded?.count),
        amount: safe(data?.dashboardStats?.refunded?.amount),
      },
      failed: {
        count: safe(data?.dashboardStats?.failed?.count),
        amount: safe(data?.dashboardStats?.failed?.amount),
      },
      online: {
        count: safe(data?.dashboardStats?.online?.count),
        amount: safe(data?.dashboardStats?.online?.amount),
      },
      offline: {
        count: safe(data?.dashboardStats?.offline?.count),
        amount: safe(data?.dashboardStats?.offline?.amount),
      },
    };
  }, []);

  // Fetch summary for Qbot transactions
  const fetchQbotSummary = useCallback(async (filters) => {
    setLoading(true);
    try {
      const res = await getTransactionSummaryAction(filters);
      if (res.status === 200) {
        setStats(mapQbotData(res.data));
      }
    } catch (error) {
      console.error("Qbot transaction summary error:", error);
      handleSnackBar("error", error?.response?.data?.message || "Failed to load transactions.");
    } finally {
      setLoading(false);
    }
  }, [mapQbotData, handleSnackBar]);


  const fetchTripolaSummary = useCallback(async (filters) => {
    console.log("FETCHING TRIPOLA SUMMARY");
    setLoading(true);
    try {

      const res = await listTripolaTransactionDataAction(
        1,
        1,
        filters.searchValue || "all",
        filters.startDate,
        filters.endDate,
        filters.status || "all",
        filters.amount || "all",
        filters.tenantFilter || "all"
      );
      // console.log("TRIPOLA RESPONSE", res);
      // console.log("TRIPOLA RESPONSE DATA", res.data);
      if (res.data.status === 200) {
        setStats(mapTripolaData(res.data));
      }
    } catch (error) {
      console.error("Tripola transaction summary error:", error);
      handleSnackBar("error", error?.response?.data?.message || "Failed to load Tripola transactions.");
    } finally {
      setLoading(false);
    }
  }, [mapTripolaData, handleSnackBar]);


  const fetchSummary = useCallback(async (filters) => {
    if (transactionType === "tripola") {
      await fetchTripolaSummary(filters);
    } else if (transactionType === "qbot") {
      await fetchQbotSummary(filters);
    }
  }, [transactionType]);

  useEffect(() => {
    if (shouldRefresh === 0) return;

    const filters = {
      startDate,
      endDate,
      status: statusFilter,
      tenantFilter,
      searchValue,
      amount: amountFilter,
    };

    fetchSummary(filters);
  }, [shouldRefresh]);

  // useEffect(() => {
  //   const filters = {
  //     startDate,
  //     endDate,
  //     status: statusFilter,
  //     tenantFilter,
  //     searchValue,
  //     amount: amountFilter,
  //   };
  //   fetchSummary(filters);

  // }, [transactionType]);


  if (transactionType === "paycentral") {
    return (
      <PaycentralTransactionCards
        handleSnackBar={handleSnackBar}
        startDate={startDate}
        endDate={endDate}
        statusFilter={statusFilter}
        tenantDb={tenantFilter}
        searchValue={searchValue}
        amountFilter={amountFilter}
        shouldRefresh={shouldRefresh}
      />
    );
  }


  const cards = [
    {
      title: "Total Transactions",
      count: stats.total.count,
      amount: stats.total.amount,
      icon: MyReceiptLongIcon,
      color: "#6366f1",
      colClass: "col-12 col-sm-6 col-lg-4 mb-4",
    },
    {
      title: "Online Payments",
      count: stats.online.count,
      amount: stats.online.amount,
      icon: MyWifiIcon,
      color: "#8b5cf6",
      colClass: "col-12 col-sm-6 col-lg-4 mb-4",
    },
    {
      title: "Offline Payments",
      count: stats.offline.count,
      amount: stats.offline.amount,
      icon: MyWifiOffIcon,
      color: "#6b7280",
      colClass: "col-12 col-sm-6 col-lg-4 mb-4",
    },
    {
      title: "Successful",
      count: stats.success.count,
      amount: stats.success.amount,
      icon: MyTaskAltIcon,
      color: "#10b981",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
    {
      title: "Pending",
      count: stats.pending.count,
      amount: stats.pending.amount,
      icon: MyHourglassEmptyIcon,
      color: "#f59e0b",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
    {
      title: "Failed",
      count: stats.failed.count,
      amount: stats.failed.amount,
      icon: MyDoNotDisturbOnIcon,
      color: "#ef4444",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
    {
      title: transactionType === "tripola" ? "Refunded" : "Refund",
      count: stats.refund.count,
      amount: stats.refund.amount,
      icon: MyRefreshIcon,
      color: "#3b82f6",
      colClass: "col-12 col-sm-6 col-lg-3 mb-4",
    },
  ];


  return (
    <div className="row g-4 mb-5">
      {cards.map((c, idx) => (
        <StatCard key={idx} {...c} loading={loading} />
      ))}
    </div>
  );
};

export default TransactionCards;